import { jsxs, jsx, Fragment as Fragment$1 } from 'react/jsx-runtime';
import { useState, useEffect, useCallback, Fragment } from 'react';

// src/lib/cx.ts
function cx(...parts) {
  let out = "";
  for (const p of parts) {
    if (!p && p !== 0) continue;
    out = out ? `${out} ${p}` : String(p);
  }
  return out;
}
function Button({
  variant = "secondary",
  size = "md",
  kbd,
  className,
  children,
  type = "button",
  ...rest
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type,
      className: cx("btn", `btn-${variant}`, size === "sm" && "btn-sm", className),
      ...rest,
      children: [
        kbd ? /* @__PURE__ */ jsx("kbd", { children: kbd }) : null,
        children
      ]
    }
  );
}
var STATUS_CLASS = {
  resolved: "status-resolved",
  review: "status-review",
  open: "status-open",
  ready: "st-ready",
  warming: "st-warming",
  reconnect: "st-reconnect",
  scheduled: "st-scheduled",
  fail: "st-fail"
};
function Badge({ kind, level, status, className, children, ...rest }) {
  if (kind === "severity") {
    return /* @__PURE__ */ jsx("span", { className: cx("badge", level && `badge-${level}`, className), ...rest, children });
  }
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cx("status-badge", status && STATUS_CLASS[status], className),
      ...rest,
      children
    }
  );
}
var CLASS = {
  ok: "sla-ok",
  warning: "sla-warning",
  breach: "sla-breach"
};
function Sla({ value, state = "ok", className, ...rest }) {
  return /* @__PURE__ */ jsx("span", { className: cx("sla", CLASS[state], className), ...rest, children: value });
}
var THEME_STORAGE_KEY = "vcip-theme";
var themeBootstrap = (defaultTheme = "dark") => `(function(){try{var s=localStorage.getItem('${THEME_STORAGE_KEY}');document.documentElement.setAttribute('data-theme',(s==='dark'||s==='light')?s:'${defaultTheme}');}catch(e){}})();`;
function readTheme(defaultTheme) {
  if (typeof document !== "undefined") {
    const attr = document.documentElement.getAttribute("data-theme");
    if (attr === "dark" || attr === "light") return attr;
  }
  return defaultTheme;
}
function ThemeToggle({
  defaultTheme = "dark",
  onChange,
  className,
  "aria-label": ariaLabel = "Toggle theme",
  ...rest
}) {
  const [theme, setTheme] = useState(defaultTheme);
  useEffect(() => {
    setTheme(readTheme(defaultTheme));
  }, [defaultTheme]);
  const toggle = useCallback(() => {
    const next = readTheme(defaultTheme) === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
    }
    setTheme(next);
    onChange?.(next);
  }, [defaultTheme, onChange]);
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      className: cx("theme-btn", className),
      onClick: toggle,
      "aria-label": ariaLabel,
      ...rest,
      children: /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: theme === "dark" ? "\u263E" : "\u2600" })
    }
  );
}
var TONE = {
  default: false,
  warning: "warning",
  success: "success",
  accent: "accent"
};
function KpiCard({
  label,
  value,
  delta,
  deltaDirection = "neutral",
  tone = "default",
  alert = false,
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsxs("div", { className: cx("kpi-card", alert && "alert-state", className), ...rest, children: [
    /* @__PURE__ */ jsx("div", { className: "kpi-label", children: label }),
    /* @__PURE__ */ jsx("div", { className: cx("kpi-value", TONE[tone]), children: value }),
    delta ? /* @__PURE__ */ jsx("div", { className: cx("kpi-delta", deltaDirection), children: delta }) : null
  ] });
}
var STATE_CLASS = {
  live: "is-live",
  warming: "is-warming",
  ready: "is-warming",
  // READY is the completed-warming look
  reconnecting: "is-reconnecting",
  fail: "is-fail",
  teardown: "is-teardown"
};
var NET_LABEL = {
  1: "network poor",
  2: "network weak",
  3: "network fair",
  4: "network good",
  5: "network excellent"
};
function SessionTile({
  state,
  customer,
  regime,
  stateLabel,
  checks,
  metaLeft,
  metaRight,
  net,
  current,
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      className: cx("session-tile", STATE_CLASS[state], className),
      "aria-current": current ? "true" : void 0,
      ...rest,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "tile-top", children: [
          /* @__PURE__ */ jsx("span", { className: "tile-state", children: stateLabel }),
          regime ? /* @__PURE__ */ jsx("span", { className: cx("regime-tag", regime), children: regime.toUpperCase() }) : null
        ] }),
        /* @__PURE__ */ jsx("div", { className: "tile-customer", children: customer }),
        checks && checks.length > 0 ? /* @__PURE__ */ jsx("div", { className: "tile-checks", children: checks.map((c, i) => /* @__PURE__ */ jsx("span", { className: cx("rc", c.status), children: c.label }, `${c.label}-${i}`)) }) : null,
        metaLeft || metaRight || net ? /* @__PURE__ */ jsxs("div", { className: "tile-meta", children: [
          /* @__PURE__ */ jsx("span", { children: metaLeft }),
          /* @__PURE__ */ jsxs("span", { children: [
            metaRight,
            net ? /* @__PURE__ */ jsx("span", { className: cx("net", `net-${net}`), "aria-label": NET_LABEL[net] }) : null
          ] })
        ] }) : null
      ]
    }
  );
}
function CustodyStrip({ items, hash, className, ...rest }) {
  return /* @__PURE__ */ jsxs("div", { className: cx("custody-strip", className), role: "status", ...rest, children: [
    items.map((it, i) => /* @__PURE__ */ jsxs("span", { className: cx("cs-item", it.broken && "broken"), children: [
      it.dot ? /* @__PURE__ */ jsx("span", { className: "rec-dot" }) : null,
      it.label
    ] }, `${it.label}-${i}`)),
    hash ? /* @__PURE__ */ jsx("span", { className: "cs-hash", children: hash }) : null
  ] });
}
function EvidenceCard({
  kind,
  label,
  labelAdornment,
  state = "pass",
  status,
  score,
  confidence,
  threshold,
  fields,
  chips,
  note,
  provenance,
  gate,
  className,
  children,
  ...rest
}) {
  const approveDisabled = gate?.approveDisabled ?? state === "fail";
  return /* @__PURE__ */ jsxs("div", { className: cx("verdict-card", className), "data-state": state, "data-kind": kind, ...rest, children: [
    /* @__PURE__ */ jsxs("div", { className: "vc-head", children: [
      /* @__PURE__ */ jsx("span", { className: "vc-label", children: label }),
      labelAdornment,
      status ? /* @__PURE__ */ jsx(Badge, { kind: "status", status: status.tone, children: status.text }) : null
    ] }),
    score ? /* @__PURE__ */ jsxs("div", { className: "vc-score", children: [
      score,
      confidence ? /* @__PURE__ */ jsx("span", { className: "vc-conf mono", children: confidence }) : null
    ] }) : null,
    threshold ? /* @__PURE__ */ jsxs("div", { className: "threshold-bar", children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: cx("threshold-fill", threshold.fill ?? "pass"),
          style: { width: `${threshold.scorePct}%` }
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "threshold-marker", style: { left: `${threshold.markerPct}%` }, children: /* @__PURE__ */ jsx("span", { className: "tm-label", children: threshold.markerLabel }) })
    ] }) : null,
    chips && chips.length > 0 ? /* @__PURE__ */ jsx("div", { className: "chip-row", children: chips.map((c, i) => /* @__PURE__ */ jsx("span", { className: cx("mini-chip", c.ok && "ok"), children: c.label }, `${c.label}-${i}`)) }) : null,
    fields && fields.length > 0 ? /* @__PURE__ */ jsx("dl", { className: "vc-fields", children: fields.map((f, i) => /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("dt", { children: f.dt }),
      /* @__PURE__ */ jsx("dd", { children: f.dd })
    ] }, `${f.dt}-${i}`)) }) : null,
    note ? /* @__PURE__ */ jsx("div", { className: "vc-note", children: note }) : null,
    children,
    /* @__PURE__ */ jsx("div", { className: "vc-prov", children: provenance }),
    gate ? /* @__PURE__ */ jsxs("div", { className: "vc-gate", children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "action-chip approve",
          onClick: gate.onApprove,
          disabled: approveDisabled,
          "aria-disabled": approveDisabled || void 0,
          children: gate.approveLabel
        }
      ),
      /* @__PURE__ */ jsx("button", { type: "button", className: "action-chip reject", onClick: gate.onReject, children: gate.rejectLabel })
    ] }) : null
  ] });
}
function VerdictRow({
  time,
  label,
  right,
  discrepancy,
  onSeek,
  className,
  onClick,
  ...rest
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cx("tl-row", discrepancy && "discrepancy", discrepancy && "fail-pulse", className),
      role: "button",
      tabIndex: 0,
      onClick: (e) => {
        onSeek?.();
        onClick?.(e);
      },
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSeek?.();
        }
      },
      ...rest,
      children: [
        /* @__PURE__ */ jsx("span", { className: "tl-time mono", children: time }),
        /* @__PURE__ */ jsx("span", { className: "tl-label", children: label }),
        right ? /* @__PURE__ */ jsx("span", { children: right }) : null
      ]
    }
  );
}
function AiCard({
  title,
  confidence,
  children,
  citations,
  gateLabel = "advisory \xB7 officer decides",
  approveLabel = "\u2713 Use",
  rejectLabel = "\u2715 Dismiss",
  onApprove,
  onReject,
  draft,
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsxs("div", { className: cx("copilot-card", className), ...rest, children: [
    /* @__PURE__ */ jsxs("div", { className: "cc-head", children: [
      /* @__PURE__ */ jsx("span", { className: "cc-title", children: title }),
      /* @__PURE__ */ jsx("span", { className: "cc-conf", children: confidence })
    ] }),
    /* @__PURE__ */ jsxs("p", { className: "cc-text", children: [
      children,
      citations && citations.length > 0 ? /* @__PURE__ */ jsxs(Fragment$1, { children: [
        " ",
        citations.map((c, i) => /* @__PURE__ */ jsxs(
          "a",
          {
            className: "ai-cite",
            role: c.onClick ? "button" : void 0,
            tabIndex: c.onClick ? 0 : void 0,
            onClick: c.onClick,
            children: [
              "[",
              c.label,
              "]"
            ]
          },
          `${c.label}-${i}`
        ))
      ] }) : null
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "cc-actions", children: [
      /* @__PURE__ */ jsx("span", { className: "ai-gate", children: gateLabel }),
      /* @__PURE__ */ jsx("button", { type: "button", className: "action-chip approve", onClick: onApprove, children: approveLabel }),
      /* @__PURE__ */ jsx("button", { type: "button", className: "action-chip reject", onClick: onReject, children: rejectLabel })
    ] }),
    draft ? /* @__PURE__ */ jsxs("div", { className: "cc-draft", children: [
      /* @__PURE__ */ jsx("span", { children: draft.label }),
      draft.loading !== false ? /* @__PURE__ */ jsx("span", { className: "skeleton" }) : null,
      draft.status ? /* @__PURE__ */ jsx("span", { children: draft.status }) : null
    ] }) : null
  ] });
}
function Dataroom({ brand, className, children, ...rest }) {
  return /* @__PURE__ */ jsx("div", { className: cx("droom", className), "data-brand": brand, ...rest, children });
}

export { AiCard, Badge, Button, CustodyStrip, Dataroom, EvidenceCard, KpiCard, SessionTile, Sla, THEME_STORAGE_KEY, ThemeToggle, VerdictRow, cx, themeBootstrap };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map